import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Archive,
  Bot,
  CheckCircle2,
  Edit3,
  Eye,
  FileText,
  PauseCircle,
  Plus,
  Rocket,
  Save,
  Search,
  SlidersHorizontal,
  UploadCloud,
  X,
} from 'lucide-react';
import { useNotification } from '../../../../hooks/useNotification';
import { useSystemCharacteristics } from '../../../../services/systemCharacteristic.service';
import {
  useAdminAgent,
  useAdminAgentFileTools,
  useAdminAgentStats,
  useAdminAgents,
  useCreateSystemAgent,
  usePublishAdminAgent,
  useUpdateAdminAgent,
  useUpdateAdminAgentFileTool,
  useUpdateAdminAgentStatus,
  useUploadAdminAgentAvatar,
} from '../hooks/useAdminAgents';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import type {
  AdminAgentActiveFilter,
  AdminAgentFileToolAction,
  AdminAgentFileToolPermission,
  AdminAgentListItem,
  AdminAgentOwnerType,
  AdminAgentStatus,
} from '../types';

const PAGE_SIZE = 20;
const OWNER_OPTIONS: Array<{ value: AdminAgentOwnerType; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'user', label: 'User' },
  { value: 'internal', label: 'Internal' },
];
const STATUS_OPTIONS: Array<{ value: AdminAgentStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
  { value: 'disabled', label: 'Disabled' },
];

interface FormState {
  name: string;
  description: string;
  avatarUrl: string;
  avatarFile: File | null;
  avatarPreviewUrl: string;
  characteristicIds: string[];
  knowledgeIdsText: string;
  reason: string;
}

interface FileToolDraft {
  isActive: boolean;
  allowedActions: AdminAgentFileToolAction[];
  maxCallsPerMinute: string;
  maxCallsPerHour: string;
  maxFilesPerDay: string;
  maxGeneratedBytesPerDayMb: string;
  reason: string;
}

const emptyForm: FormState = {
  name: '',
  description: '',
  avatarUrl: '',
  avatarFile: null,
  avatarPreviewUrl: '',
  characteristicIds: [],
  knowledgeIdsText: '',
  reason: '',
};

const bytesToMbString = (value?: number) => {
  if (!value || value <= 0) return '';
  const mb = value / (1024 * 1024);
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(2);
};

const positiveIntOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const nonNegativeMbToBytesOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return Math.round(parsed * 1024 * 1024);
};

const fileToolToDraft = (tool: AdminAgentFileToolPermission): FileToolDraft => ({
  isActive: tool.isActive,
  allowedActions: tool.allowedActions,
  maxCallsPerMinute: tool.rateLimits?.maxCallsPerMinute?.toString() ?? '',
  maxCallsPerHour: tool.rateLimits?.maxCallsPerHour?.toString() ?? '',
  maxFilesPerDay:
    tool.quotas?.maxFilesPerDay?.toString() ??
    tool.quotas?.maxCallsPerDay?.toString() ??
    tool.quotas?.maxRequests?.toString() ??
    '',
  maxGeneratedBytesPerDayMb: bytesToMbString(
    tool.quotas?.maxGeneratedBytesPerDay ??
      tool.quotas?.maxBytesPerDay ??
      tool.quotas?.maxFileBytesPerDay
  ),
  reason: '',
});

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const formatDate = (value: string | null) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(value));
};

const statusColor: Record<AdminAgentStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  draft: 'warning',
  published: 'success',
  archived: 'default',
  disabled: 'default',
};

const StatCard = ({ label, value }: { label: string; value?: number }) => (
  <Box className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <Typography variant="h5" className="font-bold text-gray-900 dark:text-slate-100">
      {value ?? '-'}
    </Typography>
    <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
      {label}
    </Typography>
  </Box>
);

const FileToolPermissionPanel = ({
  tools,
  drafts,
  isLoading,
  isSaving,
  onDraftChange,
  onSave,
}: {
  tools: AdminAgentFileToolPermission[];
  drafts: Record<string, FileToolDraft>;
  isLoading: boolean;
  isSaving: boolean;
  onDraftChange: (toolId: string, patch: Partial<FileToolDraft>) => void;
  onSave: (tool: AdminAgentFileToolPermission) => void;
}) => (
  <Box className="space-y-3">
    <Box className="flex items-center justify-between gap-3">
      <Box className="flex items-center gap-2">
        <SlidersHorizontal size={18} className="text-blue-600" />
        <Typography variant="subtitle2" className="font-semibold text-gray-900 dark:text-slate-100">
          File Tools
        </Typography>
      </Box>
      {isLoading && <CircularProgress size={18} />}
    </Box>

    {!isLoading && tools.length === 0 && (
      <Box className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-gray-500 dark:border-slate-700">
        <FileText size={24} className="mx-auto mb-2" />
        <Typography variant="body2">No file tools are available</Typography>
      </Box>
    )}

    {tools.map((tool) => {
      const draft = drafts[tool.toolId];
      if (!draft) return null;

      const isReadOnlyTool =
        tool.allowedActions.includes('read') && !tool.allowedActions.includes('write');

      return (
        <Box
          key={tool.toolId}
          className="rounded-lg border border-gray-200 p-4 dark:border-slate-700"
        >
          <Box className="mb-3 flex items-start justify-between gap-3">
            <Box className="min-w-0">
              <Box className="flex flex-wrap items-center gap-2">
                <Typography className="font-semibold text-gray-900 dark:text-slate-100">
                  {tool.name}
                </Typography>
                <Chip
                  size="small"
                  color={draft.isActive ? 'success' : 'default'}
                  label={draft.isActive ? 'Active' : 'Inactive'}
                />
              </Box>
              <Typography variant="caption" className="mt-1 block text-gray-500">
                {tool.toolId}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.isActive}
                  onChange={(event) =>
                    onDraftChange(tool.toolId, { isActive: event.target.checked })
                  }
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>

          <Typography variant="body2" className="mb-3 text-gray-600 dark:text-slate-300">
            {tool.description}
          </Typography>

          <Box className="mb-3 flex flex-wrap gap-1.5">
            {tool.allowedActions.map((action) => (
              <Chip key={action} size="small" variant="outlined" label={action} />
            ))}
            {tool.formats.map((format) => (
              <Chip key={format} size="small" label={format} />
            ))}
          </Box>

          <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Calls/min"
              value={draft.maxCallsPerMinute}
              onChange={(event) =>
                onDraftChange(tool.toolId, { maxCallsPerMinute: event.target.value })
              }
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              label="Calls/hour"
              value={draft.maxCallsPerHour}
              onChange={(event) =>
                onDraftChange(tool.toolId, { maxCallsPerHour: event.target.value })
              }
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              label={isReadOnlyTool ? 'Reads/day' : 'Files/day'}
              value={draft.maxFilesPerDay}
              onChange={(event) =>
                onDraftChange(tool.toolId, { maxFilesPerDay: event.target.value })
              }
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              label="Generated MB/day"
              value={draft.maxGeneratedBytesPerDayMb}
              onChange={(event) =>
                onDraftChange(tool.toolId, { maxGeneratedBytesPerDayMb: event.target.value })
              }
              size="small"
              type="number"
              disabled={isReadOnlyTool}
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>

          <TextField
            label="Audit reason"
            value={draft.reason}
            onChange={(event) => onDraftChange(tool.toolId, { reason: event.target.value })}
            className="mt-3"
            size="small"
            multiline
            minRows={2}
            fullWidth
          />

          <Box className="mt-3 flex justify-end">
            <Button
              variant="outlined"
              startIcon={<Save size={16} />}
              onClick={() => onSave(tool)}
              disabled={isSaving}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save Tool
            </Button>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export const AdminAgentManagementPage: React.FC = () => {
  const notification = useNotification();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput.trim(), 350);
  const [ownerType, setOwnerType] = useState<AdminAgentOwnerType | ''>('');
  const [status, setStatus] = useState<AdminAgentStatus | ''>('');
  const [active, setActive] = useState<AdminAgentActiveFilter>('all');
  const [page, setPage] = useState(1);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>();
  const [statusTarget, setStatusTarget] = useState<AdminAgentListItem | null>(null);
  const [nextStatus, setNextStatus] = useState<AdminAgentStatus>('disabled');
  const [statusReason, setStatusReason] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fileToolDrafts, setFileToolDrafts] = useState<Record<string, FileToolDraft>>({});

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ownerType, status, active]);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      ownerType: ownerType || undefined,
      status: status || undefined,
      active,
      page,
      limit: PAGE_SIZE,
    }),
    [active, debouncedSearch, ownerType, page, status]
  );

  const { data, isLoading, isError } = useAdminAgents(queryParams);
  const { data: stats } = useAdminAgentStats();
  const { data: agentDetail, isFetching: isDetailLoading } = useAdminAgent(selectedAgentId);
  const { data: fileToolData, isFetching: isFileToolsLoading } = useAdminAgentFileTools(
    selectedAgentId,
    drawerMode === 'edit'
  );
  const { data: characteristics = [] } = useSystemCharacteristics();
  const createMutation = useCreateSystemAgent();
  const updateMutation = useUpdateAdminAgent();
  const statusMutation = useUpdateAdminAgentStatus();
  const publishMutation = usePublishAdminAgent();
  const avatarUploadMutation = useUploadAdminAgentAvatar();
  const fileToolMutation = useUpdateAdminAgentFileTool();

  useEffect(() => {
    return () => {
      if (form.avatarPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(form.avatarPreviewUrl);
      }
    };
  }, [form.avatarPreviewUrl]);

  useEffect(() => {
    if (drawerMode === 'edit' && agentDetail) {
      setForm({
        name: agentDetail.name,
        description: agentDetail.description || '',
        avatarUrl: agentDetail.avatarUrl || '',
        avatarFile: null,
        avatarPreviewUrl: '',
        characteristicIds: agentDetail.characteristics.map((item) => item.publicId),
        knowledgeIdsText: agentDetail.knowledges.map((item) => item.publicId).join('\n'),
        reason: '',
      });
    }
  }, [agentDetail, drawerMode]);

  useEffect(() => {
    if (drawerMode !== 'edit' || !fileToolData) {
      setFileToolDrafts({});
      return;
    }

    setFileToolDrafts(
      Object.fromEntries(fileToolData.tools.map((tool) => [tool.toolId, fileToolToDraft(tool)]))
    );
  }, [drawerMode, fileToolData]);

  const openCreate = () => {
    setSelectedAgentId(undefined);
    setForm(emptyForm);
    setDrawerMode('create');
  };

  const openEdit = (publicId: string) => {
    setSelectedAgentId(publicId);
    setDrawerMode('edit');
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedAgentId(undefined);
    setForm(emptyForm);
    setFileToolDrafts({});
  };

  const characteristicOptions = useMemo(() => {
    const options = new Map<
      string,
      { publicId: string; name: string; layer: string; status: string }
    >();

    characteristics
      .filter((item) => item.isSystem)
      .forEach((item) => {
        options.set(item.publicId, {
          publicId: item.publicId,
          name: item.name,
          layer: item.layer,
          status: item.status,
        });
      });

    agentDetail?.characteristics.forEach((item) => {
      if (!options.has(item.publicId)) {
        options.set(item.publicId, item);
      }
    });

    return Array.from(options.values());
  }, [agentDetail?.characteristics, characteristics]);

  const characteristicNameMap = useMemo(
    () => new Map(characteristicOptions.map((item) => [item.publicId, item.name])),
    [characteristicOptions]
  );

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notification.error('Avatar must be an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error('Avatar image must be 10MB or smaller');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, avatarFile: file, avatarPreviewUrl: previewUrl }));
  };

  const clearAvatar = () => {
    setForm((prev) => ({ ...prev, avatarUrl: '', avatarFile: null, avatarPreviewUrl: '' }));
  };

  const parseKnowledgeIds = () =>
    form.knowledgeIdsText
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (!form.name.trim()) {
      notification.error('Agent name is required');
      return;
    }

    if (drawerMode === 'edit' && form.reason.trim().length < 3) {
      notification.error('Reason is required for audit log');
      return;
    }

    try {
      let avatarUrl = form.avatarUrl.trim() || null;

      if (form.avatarFile) {
        const uploaded = await avatarUploadMutation.mutateAsync(form.avatarFile);
        avatarUrl = uploaded.avatarUrl;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        avatarUrl,
        characteristicIds: form.characteristicIds,
        knowledgeIds: parseKnowledgeIds(),
      };

      if (drawerMode === 'create') {
        await createMutation.mutateAsync({ ...payload, reason: form.reason.trim() || undefined });
        notification.success('System agent created');
      } else if (selectedAgentId) {
        await updateMutation.mutateAsync({
          publicId: selectedAgentId,
          data: { ...payload, reason: form.reason.trim() },
        });
        notification.success('Agent updated');
      }
      closeDrawer();
    } catch {
      notification.error('Failed to save agent');
    }
  };

  const openStatusDialog = (agent: AdminAgentListItem, targetStatus: AdminAgentStatus) => {
    setStatusTarget(agent);
    setNextStatus(targetStatus);
    setStatusReason('');
  };

  const handleStatusConfirm = async () => {
    if (!statusTarget || statusReason.trim().length < 3) {
      notification.error('Reason is required for audit log');
      return;
    }

    try {
      await statusMutation.mutateAsync({
        publicId: statusTarget.publicId,
        data: { status: nextStatus, reason: statusReason.trim() },
      });
      notification.success('Agent status updated');
      setStatusTarget(null);
    } catch {
      notification.error('Failed to update agent status');
    }
  };

  const handlePublish = async (publicId: string) => {
    try {
      await publishMutation.mutateAsync(publicId);
      notification.success('Agent published');
    } catch {
      notification.error('Failed to publish agent');
    }
  };

  const updateFileToolDraft = (
    toolId: string,
    patch: Partial<FileToolDraft> | ((draft: FileToolDraft) => Partial<FileToolDraft>)
  ) => {
    setFileToolDrafts((prev) => {
      const current = prev[toolId];
      if (!current) return prev;
      const nextPatch = typeof patch === 'function' ? patch(current) : patch;
      return {
        ...prev,
        [toolId]: {
          ...current,
          ...nextPatch,
        },
      };
    });
  };

  const handleSaveFileTool = async (tool: AdminAgentFileToolPermission) => {
    if (!selectedAgentId) return;
    const draft = fileToolDrafts[tool.toolId];
    if (!draft) return;

    if (draft.reason.trim().length < 3) {
      notification.error('Audit reason is required for file tool changes');
      return;
    }

    const maxCallsPerMinute = positiveIntOrUndefined(draft.maxCallsPerMinute);
    const maxCallsPerHour = positiveIntOrUndefined(draft.maxCallsPerHour);
    const maxFilesPerDay = positiveIntOrUndefined(draft.maxFilesPerDay);
    const maxGeneratedBytesPerDay = nonNegativeMbToBytesOrUndefined(
      draft.maxGeneratedBytesPerDayMb
    );

    if (
      (draft.maxCallsPerMinute.trim() && !maxCallsPerMinute) ||
      (draft.maxCallsPerHour.trim() && !maxCallsPerHour) ||
      (draft.maxFilesPerDay.trim() && !maxFilesPerDay) ||
      (draft.maxGeneratedBytesPerDayMb.trim() && maxGeneratedBytesPerDay === undefined)
    ) {
      notification.error('File tool limits must be valid numbers');
      return;
    }

    try {
      await fileToolMutation.mutateAsync({
        publicId: selectedAgentId,
        toolId: tool.toolId,
        data: {
          isActive: draft.isActive,
          allowedActions: draft.allowedActions,
          rateLimits: {
            ...(maxCallsPerMinute ? { maxCallsPerMinute } : {}),
            ...(maxCallsPerHour ? { maxCallsPerHour } : {}),
          },
          quotas: {
            ...(maxFilesPerDay ? { maxFilesPerDay } : {}),
            ...(maxGeneratedBytesPerDay !== undefined ? { maxGeneratedBytesPerDay } : {}),
          },
          reason: draft.reason.trim(),
        },
      });
      notification.success('File tool permission updated');
      updateFileToolDraft(tool.toolId, { reason: '' });
    } catch {
      notification.error('Failed to update file tool permission');
    }
  };

  const agents = data?.agents ?? [];
  const meta = data?.pagination;
  const currentAvatarUrl = form.avatarPreviewUrl || form.avatarUrl || undefined;

  return (
    <Box className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <Box className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            Agent Management
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
            Manage system agents and user-owned agents with audited admin actions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New System Agent
        </Button>
      </Box>

      <Box className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Total" value={stats?.totalAgents} />
        <StatCard label="System" value={stats?.systemAgents} />
        <StatCard label="User" value={stats?.userAgents} />
        <StatCard label="Active" value={stats?.activeAgents} />
        <StatCard label="Disabled" value={stats?.disabledAgents} />
        <StatCard label="Published" value={stats?.publishedAgents} />
      </Box>

      <Box className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Box className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name, ID, owner email..."
            className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Owner</InputLabel>
          <Select
            value={ownerType}
            label="Owner"
            onChange={(e) => setOwnerType(e.target.value as AdminAgentOwnerType | '')}
          >
            <MenuItem value="">All owners</MenuItem>
            {OWNER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value as AdminAgentStatus | '')}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Active</InputLabel>
          <Select
            value={active}
            label="Active"
            onChange={(e) => setActive(e.target.value as AdminAgentActiveFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        {isLoading ? (
          <Box className="flex h-64 items-center justify-center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box className="flex h-64 items-center justify-center text-gray-500">
            Failed to load agents
          </Box>
        ) : agents.length === 0 ? (
          <Box className="flex h-64 flex-col items-center justify-center gap-3 text-gray-500">
            <Bot size={40} />
            <Typography>No agents match the current filters</Typography>
          </Box>
        ) : (
          <Box className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-left text-sm">
              <thead className="border-b border-gray-200 bg-slate-50 text-xs uppercase text-gray-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Links</th>
                  <th className="px-4 py-3">Tokens</th>
                  <th className="px-4 py-3">Usage</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {agents.map((agent) => (
                  <tr key={agent.publicId} className="text-gray-800 dark:text-slate-200">
                    <td className="px-4 py-3">
                      <Box className="flex items-center gap-3">
                        <Avatar src={agent.avatarUrl || undefined} sx={{ width: 36, height: 36 }}>
                          <Bot size={18} />
                        </Avatar>
                        <Box className="min-w-0">
                          <Typography className="truncate font-semibold">{agent.name}</Typography>
                          <Typography variant="caption" className="text-gray-500">
                            {agent.publicId}
                          </Typography>
                        </Box>
                      </Box>
                    </td>
                    <td className="px-4 py-3">
                      <Box className="space-y-1">
                        <Chip size="small" label={agent.ownerType} variant="outlined" />
                        <Typography variant="caption" className="block text-gray-500">
                          {agent.owner.email}
                        </Typography>
                      </Box>
                    </td>
                    <td className="px-4 py-3">
                      <Chip size="small" color={statusColor[agent.status]} label={agent.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Typography variant="body2">Draft v{agent.version}</Typography>
                      <Typography variant="caption" className="text-gray-500">
                        Published {agent.publishedVersion ? `v${agent.publishedVersion}` : '-'}
                      </Typography>
                    </td>
                    <td className="px-4 py-3">
                      <Typography variant="body2">{agent.characteristicCount} chars</Typography>
                      <Typography variant="caption" className="text-gray-500">
                        {agent.knowledgeCount} knowledge
                      </Typography>
                    </td>
                    <td className="px-4 py-3">
                      <Typography variant="body2" className="font-semibold">
                        {formatAdminNumber(agent.totalTokensUsed)}
                      </Typography>
                      <Typography variant="caption" className="text-gray-500">
                        total used
                      </Typography>
                    </td>
                    <td className="px-4 py-3">
                      <Typography variant="body2">{agent.chatSessionCount} sessions</Typography>
                      <Typography variant="caption" className="text-gray-500">
                        {agent.usageLogCount} logs
                      </Typography>
                    </td>
                    <td className="px-4 py-3">{formatDate(agent.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <Box className="flex justify-end gap-1">
                        <Tooltip title="Edit">
                          <Button size="small" onClick={() => openEdit(agent.publicId)}>
                            <Edit3 size={16} />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Publish">
                          <Button size="small" onClick={() => handlePublish(agent.publicId)}>
                            <Rocket size={16} />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Disable">
                          <Button size="small" onClick={() => openStatusDialog(agent, 'disabled')}>
                            <PauseCircle size={16} />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Archive">
                          <Button size="small" onClick={() => openStatusDialog(agent, 'archived')}>
                            <Archive size={16} />
                          </Button>
                        </Tooltip>
                        {!agent.isActive && (
                          <Tooltip title="Activate as draft">
                            <Button size="small" onClick={() => openStatusDialog(agent, 'draft')}>
                              <CheckCircle2 size={16} />
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {meta && meta.totalPages > 1 && (
        <Box className="mt-5 flex justify-center">
          <Pagination count={meta.totalPages} page={page} onChange={(_, value) => setPage(value)} />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={drawerMode !== null}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      >
        <Box className="flex h-full flex-col bg-white dark:bg-slate-800">
          <Box className="border-b border-gray-200 p-5 dark:border-slate-700">
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              {drawerMode === 'create' ? 'Create System Agent' : 'Edit Agent'}
            </Typography>
            {drawerMode === 'edit' && agentDetail && (
              <Typography variant="caption" className="text-gray-500">
                Owner: {agentDetail.owner.email} | {agentDetail.ownerType}
              </Typography>
            )}
          </Box>
          <Box className="flex-1 space-y-4 overflow-y-auto p-5">
            {isDetailLoading && drawerMode === 'edit' ? (
              <Box className="flex h-40 items-center justify-center">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TextField
                  label="Agent name"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  fullWidth
                />
                <Box className="rounded-lg border border-gray-200 p-4 dark:border-slate-700">
                  <Typography
                    variant="subtitle2"
                    className="mb-3 font-semibold text-gray-900 dark:text-slate-100"
                  >
                    Avatar
                  </Typography>
                  <Box className="flex items-center gap-4">
                    <Avatar src={currentAvatarUrl} sx={{ width: 72, height: 72 }}>
                      <Bot size={28} />
                    </Avatar>
                    <Box className="min-w-0 flex-1">
                      <Box className="flex flex-wrap gap-2">
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<UploadCloud size={16} />}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Upload image
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarFileChange}
                          />
                        </Button>
                        {currentAvatarUrl && (
                          <Button
                            variant="text"
                            color="error"
                            startIcon={<X size={16} />}
                            onClick={clearAvatar}
                            sx={{ textTransform: 'none' }}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                      <Typography variant="caption" className="mt-2 block truncate text-gray-500">
                        {form.avatarFile
                          ? form.avatarFile.name
                          : form.avatarUrl
                            ? 'Current avatar'
                            : 'PNG, JPG, GIF, WebP, or SVG up to 10MB'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>System Characteristics</InputLabel>
                  <Select
                    multiple
                    value={form.characteristicIds}
                    label="System Characteristics"
                    renderValue={(selected) => {
                      const selectedIds = selected as string[];
                      if (selectedIds.length === 0) return 'No characteristics selected';

                      return (
                        <Box className="flex flex-wrap gap-1">
                          {selectedIds.map((id) => (
                            <Chip
                              key={id}
                              size="small"
                              label={characteristicNameMap.get(id) || id}
                            />
                          ))}
                        </Box>
                      );
                    }}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        characteristicIds: event.target.value as string[],
                      }))
                    }
                  >
                    {characteristicOptions.map((item) => (
                      <MenuItem key={item.publicId} value={item.publicId}>
                        <Checkbox checked={form.characteristicIds.includes(item.publicId)} />
                        <ListItemText
                          primary={item.name}
                          secondary={`${item.layer} | ${item.status}`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {form.characteristicIds.length > 0 && (
                  <Box className="flex flex-wrap gap-2">
                    {form.characteristicIds.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        variant="outlined"
                        label={characteristicNameMap.get(id) || id}
                      />
                    ))}
                  </Box>
                )}
                <TextField
                  label="Knowledge public IDs"
                  value={form.knowledgeIdsText}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, knowledgeIdsText: event.target.value }))
                  }
                  helperText="One ID per line. User agents can only use system knowledge or their owner's knowledge."
                  multiline
                  minRows={4}
                  fullWidth
                />
                {drawerMode === 'edit' && (
                  <>
                    <Divider />
                    <FileToolPermissionPanel
                      tools={fileToolData?.tools ?? []}
                      drafts={fileToolDrafts}
                      isLoading={isFileToolsLoading}
                      isSaving={fileToolMutation.isPending}
                      onDraftChange={updateFileToolDraft}
                      onSave={handleSaveFileTool}
                    />
                  </>
                )}
                <Divider />
                <TextField
                  label={drawerMode === 'edit' ? 'Audit reason' : 'Reason'}
                  value={form.reason}
                  onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                  helperText={
                    drawerMode === 'edit'
                      ? 'Required. Stored in audit log.'
                      : 'Optional. Stored in audit log.'
                  }
                  multiline
                  minRows={2}
                  fullWidth
                />
              </>
            )}
          </Box>
          <Box className="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-slate-700">
            <Button onClick={closeDrawer} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Eye size={16} />}
              onClick={handleSave}
              disabled={
                avatarUploadMutation.isPending ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(statusTarget)}
        onClose={() => setStatusTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Agent Status</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
          <Typography variant="body2" className="text-gray-600">
            Change {statusTarget?.name} to {nextStatus}.
          </Typography>
          <TextField
            label="Audit reason"
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusConfirm}
            disabled={statusMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
